import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { mapLanguageToComponentProps } from 'interview/i18n'
import Attachments from 'components/attachments/attachments'
import FormInput from 'components/form-components/form-input'
import { postAttachment } from 'interview/actions/candidate'
import CandidateService from 'interview/services/candidate'
import AdditionalFieldsForm from 'components/additional-fields-form'
import PhoneInput from 'components/phone-input'
import FormGroup from 'components/form-components/form-group'
import { validators } from 'utils/form'
import { Body, H3, Label } from 'components/styled/typography'
import FormErrors from 'components/form-components/form-errors'

const propTypes = {
  changeAction: PropTypes.func,
  candidate: PropTypes.shape({
    additionalfieldResponses: PropTypes.arrayOf(
      PropTypes.shape({
        fieldId: PropTypes.string,
        answer: PropTypes.string,
      })
    ),
  }).isRequired,
  additionalFormFields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
      required: PropTypes.bool,
    })
  ).isRequired,
  formSetup: PropTypes.shape({
    name: PropTypes.shape({
      required: PropTypes.bool,
      visible: PropTypes.bool,
    }),
    email: PropTypes.shape({
      required: PropTypes.bool,
      visible: PropTypes.bool,
    }),
    phoneNumber: PropTypes.shape({
      required: PropTypes.bool,
      visible: PropTypes.bool,
    }),
    city: PropTypes.shape({
      required: PropTypes.bool,
      visible: PropTypes.bool,
    }),
    links: PropTypes.shape({
      required: PropTypes.bool,
      visible: PropTypes.bool,
    }),
    attachments: PropTypes.shape({
      required: PropTypes.bool,
      visible: PropTypes.bool,
      description: PropTypes.string,
    }),
  }),
  disableEmail: PropTypes.bool,
  publicInterview: PropTypes.bool,
  i18n: PropTypes.shape({
    interviewForm: PropTypes.object,
  }).isRequired,
  isFormValid: PropTypes.func.isRequired,
  language: PropTypes.string,
  model: PropTypes.string,
  dispatch: PropTypes.func,
}

const defaultProps = {
  changeAction: () => {},
  disableEmail: false,
  publicInterview: false,
  dispatch: undefined,
  formSetup: {
    name: {
      required: false,
      visible: false,
    },
    email: {
      required: false,
      visible: false,
    },
    phoneNumber: {
      required: false,
      visible: false,
    },
    city: {
      required: false,
      visible: false,
    },
    links: {
      required: false,
      visible: false,
    },
    attachments: {
      required: false,
      visible: false,
    },
  },
  language: 'en',
  model: 'candidate',
}

export class InterviewForm extends Component {
  addProtocol(value) {
    return value.length === 0 || value.startsWith('http')
      ? value
      : `http://${value}`
  }

  onFileAdd(data) {
    const { candidate, dispatch, model, publicInterview } = this.props
    if (!publicInterview && model !== 'previewCandidate') {
      dispatch(postAttachment(`${model}.attachments`, candidate.uuid, data))
    }
  }

  onFileRemove(key) {
    const { candidate, publicInterview, model } = this.props
    if (!publicInterview && model !== 'previewCandidate') {
      CandidateService.deleteAttachment(candidate.uuid, { key })
    }
  }

  render() {
    const {
      disableEmail,
      publicInterview,
      i18n,
      changeAction,
      formSetup,
      isFormValid,
      candidate,
      additionalFormFields,
      language,
      model,
    } = this.props

    const { isRequired, isEmail } = validators
    const visibleFields = additionalFormFields.filter(
      field => field.fieldSetup.visible
    )

    // TODO replace link label with i18n after translations are ready
    return (
      <>
        <H3>{i18n.interviewForm.legendBasicInfo}</H3>
        <Body>{i18n.interviewForm.required}</Body>
        {formSetup.name.visible && (
          <FormInput
            model={`${model}.name`}
            label={`${i18n.interviewForm.name}${
              formSetup.name.required ? '*' : ''
            }`}
            validators={formSetup.name.required ? { isRequired } : null}
            type="text"
            placeholder={
              i18n.interviewForm.placeholderName +
              (formSetup.name.required ? '*' : '')
            }
            updateOn="blur"
            changeAction={changeAction}
          />
        )}

        {formSetup.email.visible && (
          <FormInput
            className={disableEmail ? 'disabled' : undefined}
            model={`${model}.email`}
            label={`${i18n.interviewForm.email}${
              formSetup.email.required ? '*' : ''
            }`}
            validators={
              formSetup.email.required ? { isRequired, isEmail } : { isEmail }
            }
            type="email"
            placeholder={
              i18n.interviewForm.placeholderEmail +
              (formSetup.email.required ? '*' : '')
            }
            updateOn="blur"
            changeAction={changeAction}
            readOnly={disableEmail}
          />
        )}

        {formSetup.phoneNumber.visible && (
          <FormGroup model={`${model}.phone`} name={`${model}.phone`}>
            <Label color="grey90">{`${i18n.interviewForm.phone}${
              formSetup.phoneNumber.required ? '*' : ''
            }`}</Label>
            <PhoneInput
              model={`${model}.phone`}
              onBlur={(value, isValid) =>
                isValid && changeAction(`${model}.phone`, value)
              }
              isRequired={formSetup.phoneNumber.required}
              useRtl={language === 'ar'}
            />
            <FormErrors model={`${model}.phone`} />
          </FormGroup>
        )}

        {formSetup.city.visible && (
          <FormInput
            model={`${model}.city`}
            label={`${i18n.interviewForm.city}${
              formSetup.city.required ? '*' : ''
            }`}
            validators={formSetup.city.required ? { isRequired } : null}
            type="text"
            placeholder={
              i18n.interviewForm.placeholderCity +
              (formSetup.city.required ? '*' : '')
            }
            updateOn="blur"
            changeAction={changeAction}
          />
        )}
        {formSetup.links.visible && (
          <FormInput
            model={`${model}.link`}
            label={`${i18n.interviewForm.link}${
              formSetup.links.required ? '*' : ''
            }`}
            validators={formSetup.links.required ? { isRequired } : null}
            type="text"
            placeholder={
              i18n.interviewForm.placeholderLink +
              (formSetup.links.required ? '*' : '')
            }
            updateOn="blur"
            validateOn="blur"
            changeAction={(model, value) =>
              changeAction(model, this.addProtocol(value))
            }
          />
        )}
        {formSetup.attachments.visible && (
          <Attachments
            model={`${model}.attachments`}
            description={formSetup.attachments.description}
            limit={5}
            valid="true"
            onAdd={data => this.onFileAdd(data)}
            onRemove={key => this.onFileRemove(key)}
            isRequired={formSetup.attachments.required}
            publicInterview={publicInterview || model === 'previewCandidate'}
          />
        )}
        {visibleFields.length > 0 && (
          <AdditionalFieldsForm
            requiredText={i18n.interviewForm.required}
            legend={i18n.interviewForm.legendAdditionalFields}
            additionalFormFields={visibleFields}
            responses={candidate.additionalFieldResponses}
            changeAction={changeAction}
            isFormValid={isFormValid}
          />
        )}
      </>
    )
  }
}

InterviewForm.propTypes = propTypes
InterviewForm.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps())(InterviewForm)
